import { Component, computed, effect, inject, input, InputSignal } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { ClassificationView } from "@bitwarden/web-vault/app/models/view/classification/classification.view";
import { MatDialogRef } from "@angular/material/dialog";
import { ClassificationUtils } from "@bitwarden/web-vault/app/models/view/classification/classification.utils";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";

@Component({
  selector: "app-classification-add-edit",
  templateUrl: "./classification-add-edit.component.html",
})
export class ClassificationAddEditComponent {
  form: FormGroup;
  loading = false;
  classification: InputSignal<ClassificationView> = input(null);
  actionSucceeded: InputSignal<CallableFunction> = input<CallableFunction>(null);
  originalClassificationName: string = "";

  private i18nService: I18nService = inject(I18nService);
  private userStore: UserStoreService = inject(UserStoreService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private classificationUtils: ClassificationUtils = inject(ClassificationUtils);

  protected readonly saveButtonOptions = new ActionButton({
    text: this.i18nService.t("save"),
    isEnabled: true,
    type: "submit",
    onClick: this.submit.bind(this),
  });

  constructor(
    private dialogRef: MatDialogRef<ClassificationAddEditComponent>,
    private globalService: GlobalService,
    private formBuilder: FormBuilder,
  ) {
    this.initializeForm();
    effect(() => {
      if (this.classification()) {
        this.originalClassificationName = this.classification().name;
        this.patchClassificationName(this.originalClassificationName);
      }
    });
  }

  editMode = computed(() => this.classification() != null);

  initializeForm() {
    this.form = this.formBuilder.group({
      classificationName: ["", Validators.required],
    });
  }

  async submit() {
    try {
      if (this.form.valid) {
        if (this.editMode()) {
          await this.updateClassification();
        } else {
          await this.createClassification();
        }
      } else {
        this.saveButtonOptions.enableButton(true);
        this.globalService.showWarningMessage("warning", "classificationNameInfo");
      }
    } catch (e) {
      this.globalService.showErrorMessage("errorOccurred", "somethingWentWrong");
    }
  }

  hasErrorInClassificationUtil(error: Error) {
    this.notifyService.notify(NotificationEnum.error, "errorOccurred", error.message, true);
    this.patchClassificationName(this.originalClassificationName);
    this.saveButtonOptions.enableButton(true);
  }

  async createClassification() {
    try {
      const newName = this.form.controls.classificationName.value;
      await this.classificationUtils.addNewClassification(newName, this.userStore);
      this.successfulMessage(newName);
    } catch (e) {
      this.hasErrorInClassificationUtil(e);
    }
  }

  patchClassificationName(name: string) {
    this.form.patchValue({ classificationName: name });
  }

  isNameUnchanged() {
    return this.form.controls.classificationName.value === this.originalClassificationName;
  }

  async updateClassification() {
    if (this.isNameUnchanged()) {
      this.successfulMessage(this.originalClassificationName);
      return;
    }

    const inStore = this.userStore.classifications.classificationViewsSorted();
    try {
      const toUpdate = inStore.find((c) => c.id === this.classification().id)?.clone();
      if (!toUpdate) {
        throw new Error("Classification no longer exists");
      }
      toUpdate.name = this.form.controls.classificationName.value;

      const updated = await this.classificationUtils.updateClassificationName(
        toUpdate.name,
        toUpdate,
        this.userStore,
      );
      if (updated instanceof ClassificationView) {
        this.successfulMessage(updated.name);
      }
    } catch (e) {
      this.hasErrorInClassificationUtil(e);
    }
  }

  successfulMessage(name: string) {
    const successfulResponse = {
      newClassificationText: name,
      successfulText: this.editMode() ? "updatedSuccessfully" : "createdSuccessfully",
    };
    this.actionSucceeded()?.(successfulResponse);
    this.dialogRef.close();
  }

  onClassificationNameChange(name: string) {
    this.form.controls.classificationName.setValue(name);
  }
}
